/**
 * SocialPulseCallback — sister-app landing page for the OIDC redirect.
 * Reads ?code=&state= from the URL, trades them for a session cookie,
 * then hops the user to `returnTo` (default "/"). On error we surface a
 * friendly message + a "Try again" CTA that returns them to /login.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { finishSocialPulseLogin } from "@/lib/socialpulseOidc";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function SocialPulseCallback() {
  const [status, setStatus] = useState({ state: "loading", message: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { user, token, returnTo } = await finishSocialPulseLogin(API);
        if (cancelled) return;
        try {
          if (token) localStorage.setItem("wofy_token", token);
          if (user) localStorage.setItem("wofy_user", JSON.stringify(user));
        } catch {}
        setStatus({ state: "ok", message: `Welcome, ${user?.name || "friend"} — one moment…` });
        // Full-page navigation ensures the fresh cookie is used on the
        // first request to the destination page.
        window.location.replace(returnTo || "/");
      } catch (err) {
        if (cancelled) return;
        setStatus({
          state: "error",
          message: (err && err.message) || "Something went wrong signing you in.",
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      data-testid="socialpulse-callback"
      className="max-w-lg mx-auto px-6 py-24 text-center"
      style={{ color: "var(--wofy-text)" }}
    >
      {status.state === "loading" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto animate-spin opacity-70" aria-hidden />
          <h1 className="mt-6 text-2xl md:text-3xl font-serif font-semibold">
            Signing you in with SocialPulse…
          </h1>
          <p className="mt-3 text-base opacity-75">
            One moment while we complete the handshake.
          </p>
        </>
      )}
      {status.state === "ok" && (
        <>
          <div
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-full mb-4"
            style={{ background: "var(--wofy-accent-soft, #FDF3E7)", color: "var(--wofy-primary)" }}
          >
            Signed in
          </div>
          <h1 className="text-2xl md:text-3xl font-serif font-semibold">{status.message}</h1>
        </>
      )}
      {status.state === "error" && (
        <>
          <AlertCircle className="w-10 h-10 mx-auto opacity-80" style={{ color: "#B91C1C" }} aria-hidden />
          <h1 className="mt-6 text-2xl md:text-3xl font-serif font-semibold">
            We couldn't finish signing you in
          </h1>
          <p className="mt-3 text-base opacity-80" data-testid="socialpulse-callback-error">
            {status.message}
          </p>
          <Link
            to="/login"
            data-testid="socialpulse-callback-retry"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold mt-8 shadow-sm hover:opacity-90"
            style={{ background: "var(--wofy-primary)", color: "#FDFBF7" }}
          >
            Try again
          </Link>
        </>
      )}
    </section>
  );
}
